import React from 'react';
import { forEach, isNil } from 'lodash';
import useImageUploadMutate from './useImageUploadMutate';
import { handleError, shouldSquareImage, validateUploadImage } from '../utils';
import { Attachment, Restriction } from '../types';

interface IUseImageUploadProps {
  attachmentList: Attachment[];
  uploadSuccessAttachment: (data: Attachment, index: number) => void;
  maxCnt: number;
  enableGif: boolean;
  capacityLimit?: number;
  cropMode: boolean;
  restriction?: Restriction;
  setCropFile: React.Dispatch<React.SetStateAction<{ index: number; file: File } | null>>;
}
const useImageUpload = (props: IUseImageUploadProps) => {
  const {
    attachmentList,
    uploadSuccessAttachment,
    maxCnt,
    enableGif,
    capacityLimit,
    cropMode,
    restriction,
    setCropFile,
  } = props;

  const { squareImageUploadMutate, rectangleImageUploadMutate } = useImageUploadMutate(uploadSuccessAttachment);

  const uploadCroppedImage = (file: File, index: number) => {
    squareImageUploadMutate({ file, index });
    setCropFile(null);
  };

  const uploadImage = async (e: React.ChangeEvent<HTMLInputElement>, updateTargetIndex?: number) => {
    e.preventDefault();

    try {
      if (e.target && e.target.files) {
        const files = e.target.files;

        await validateUploadImage({
          isUpdateImage: !isNil(updateTargetIndex),
          files,
          attachmentList,
          maxCnt,
          enableGif,
          capacityLimit,
          cropMode,
          restriction,
        });

        if (cropMode && !(await shouldSquareImage(files[0]))) {
          const uploadPositionIndex = !isNil(updateTargetIndex) ? updateTargetIndex : attachmentList.length;

          setCropFile({ index: uploadPositionIndex, file: files[0] });
          return;
        }

        forEach(files, async (file, index) => {
          const uploadPositionIndex = !isNil(updateTargetIndex) ? updateTargetIndex : attachmentList.length + index;

          if (await shouldSquareImage(file)) {
            squareImageUploadMutate({ file, index: uploadPositionIndex });
          } else {
            rectangleImageUploadMutate({ file, index: uploadPositionIndex });
          }
        });
      }
    } catch (e: unknown) {
      // 안전한 타입 확인
      if (e instanceof Error) {
        console.error(e);
        await handleError(e.message);
      } else {
        // 알 수 없는 타입의 오류 처리
        console.error('An unknown error occurred');
      }
    } finally {
      e.target.value = '';
    }
  };

  return {
    uploadImage,
    uploadCroppedImage,
  };
};

export default useImageUpload;
